<template>
  <div class="date_calendar rangePicker">


    <div class="row d-flex" :class="{'flex-row': !alwaysUnder}">

      <div class="col-12" :class="{'col-lg-6': !alwaysUnder}">
        <div class="header prev">
          <div class="col-3 arrow-left">
            <button @click="togglePreviousMonth()" type="button"
                    class="btn btn-light left d-flex align-items-center justify-content-center">
              <TyIcon icon="ty-arrow-left pt-1" width="1rem"/>
            </button>
          </div>
          <div class="col-6 month-year">
            <p class="header-date m-o">
              <span class="month">{{ months[language].find(m => (m.num - 1) === currentMonth).long }}</span>
              {{ currentYear }}
            </p>
          </div>

        </div>
        <div class="calendar">
          <div class="thead">
            <div class="day_line">
              <div v-for="i in [0,1,2,3,4,5,6]" class="col">
                {{ days[language].two[i] }}
              </div>
            </div>
          </div>
          <div class="tbody">
            <div class="week_line" v-for="weekNumber in weekNumbers(currentYear, currentMonth)">
              <div v-for="day in weekDays(weekNumber,startWeekA, currentYear)" class="col day"
                   :class="{
          'different_month': day.month() !== currentMonth,
          'disabled': isDateDisabled(day),
          'reserved': isDateReserved(day.formatted),
          'highlight-prev': isDateReservedBefore(day),
          'highlight-after': isDateReservedAfter(day),
          'duration_selected': isDateRangeSelected(day),
          'duration': isDateBetweenStart(day),
          'is_weekend': isDateWeekend(day),
          'is_holiday': isDateHoliday(day.formatted),
          'first': ((dateRange.startDate && dateRange.startDate.diff(day) === 0) || (dateRange.startDate === null && selectedDay && selectedDay.diff(day) === 0)),
          'last': ((dateRange.endDate && dateRange.endDate.diff(day) === 0) || (dateRange.startDate === null && hoverDay && hoverDay.diff(day) === 0))
        }"
                   @mouseover="selectHoverDay(day)">
                <div class="in"
                     @click="selectDay(day)">
                  {{ day.format('DD') }}
                </div>

                <span class="holiday" v-if="isDateHoliday(day.formatted)" :title="getHolidayName(day.formatted)">
                  {{ $t('holiday') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" :class="{'col-lg-6': !alwaysUnder}">
        <div class="header next" :class="alwaysUnder ? ' mt-lg-3 ' : 'mt-lg-0'">
          <div class="col-6 month-year">
            <p class="header-date m-o">
              <span class="month">{{ months[language].find(m => (m.num - 1) === nextMonth).long }}</span> {{ nextYear }}
            </p>
          </div>


          <div class="col-3 arrow-right">
            <button @click="toggleNextMonth()" type="button"
                    class="btn btn-light right d-flex align-items-center justify-content-center">
              <TyIcon icon="ty-arrow-right pt-1" width="1rem"/>
            </button>
          </div>
        </div>
        <div class="calendar">
          <div class="thead">
            <div class="day_line">
              <div v-for="i in [0,1,2,3,4,5,6]" class="col">
                {{ days[language].two[i] }}
              </div>
            </div>
          </div>
          <div class="tbody">
            <div class="week_line" v-for="weekNumber in weekNumbers(nextYear, nextMonth)">
              <div v-for="day in weekDays(weekNumber,startWeekB, nextYear)" class="col day"
                   :class="{
          'different_month': day.month() !== nextMonth,
          'disabled': isDateDisabled(day),
          'highlight-prev': isDateReservedBefore(day),
          'highlight-after': isDateReservedAfter(day),
          'reserved': isDateReserved(day.formatted),
          'duration_selected': isDateRangeSelected(day),
          'duration': isDateBetweenStart(day),
          'is_weekend': isDateWeekend(day),
          'is_holiday': isDateHoliday(day.formatted),
          'first': ((dateRange.startDate && dateRange.startDate.diff(day) === 0) || (dateRange.startDate === null && selectedDay && selectedDay.diff(day) === 0)),
          'last': ((dateRange.endDate && dateRange.endDate.diff(day) === 0) || (dateRange.startDate === null && hoverDay && hoverDay.diff(day) === 0))
        }"
                   @mouseover="selectHoverDay(day)">
                <div class="in"
                     @click="selectDay(day)">
                  {{ day.format('DD') }}
                </div>
                <span class="holiday" v-if="isDateHoliday(day.formatted)" :title="getHolidayName(day.formatted)">
                  {{ $t('holiday') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <p v-if="dateRange.startDate !== null" class="text-center mt-3 mb-0">
      {{ dateRange.startDate.format('DD.MM.YYYY') }} - {{ dateRange.endDate.format('DD.MM.YYYY') }}
      ({{ dayjs(dateRange.endDate.format('YYYY-MM-DD')).diff(dayjs(dateRange.startDate.format('YYYY-MM-DD')), 'day') }}
      {{
        $t($vysklonujSlovo('noc', dayjs(dateRange.endDate.format('YYYY-MM-DD')).diff(dayjs(dateRange.startDate.format('YYYY-MM-DD')), 'day'), 'i', 'och'))
      }})

    </p>

    <PartsTempPreloader v-if="preloader"/>

    <Legend v-if="props.showLegend"/>

  </div>
</template>

<script setup lang="ts">
import "~/assets/less/calendar/calendar_date_range_picker.min.css";
import weekOfYear from "dayjs/plugin/weekOfYear";
import dayjs from "dayjs";
import {computed, ref} from "vue";
import {clearObserverObj, weekCount} from "../../../../lib/functions";
import {usePicker} from "~/composables/calendar_picker";
import {useNuxtApp} from "#app";
import months from "~/lib/data/months.json";
import days from "~/lib/data/days.json";
import Legend from "./legend.vue";
import {obtainActiveLocale} from "../../../../composables/master_page";

const props = defineProps({
  startDate: dayjs.Dayjs,
  minDate: dayjs.Dayjs,
  minDuration: Number,
  disabled_dates: Array,
  alwaysUnder: Boolean,
  showLegend: Boolean,
  prevent_select: Boolean
});

const {$vysklonujSlovo} = useNuxtApp();
const emit = defineEmits(['onSelect', 'clickAction']);

const activeLanguage = obtainActiveLocale();
const currentMonth = ref(0);
const currentYear = ref(2021);
const preloader = ref(false);
const language = ref(activeLanguage.code);


const {
  getPossibleAvailableDay,
  selectedDay,
  isDateReserved,
  dateRange,
  isDateDisabled,
  isDateReservedBefore,
  isDateReservedAfter,
  hoverDay,
  isDateHoliday,
  getHolidayName,
  isDateBetweenStart,
  isDateWeekend,
  isDateRangeSelected,
  setSelectedDates
} = usePicker(props.minDate, props.disabled_dates, null);

const startWeekB = computed(() => {
  dayjs.extend(weekOfYear);
  if (currentMonth.value > 11) {
    return dayjs().year(currentYear.value + 1).month(1).date(1).week();
  } else {
    return dayjs().year(currentYear.value).month(currentMonth.value).add(1, 'month').date(1).week();
  }
});

const startWeekA = computed(() => {
  dayjs.extend(weekOfYear);
  return dayjs().year(currentYear.value).month(currentMonth.value).date(1).week();
});
const nextMonth = computed(() => {
  return currentMonth.value > 10 ? 0 : (currentMonth.value + 1);
});
const nextYear = computed(() => {
  return nextMonth.value < currentMonth.value ? (currentYear.value + 1) : currentYear.value;
});

if (props.startDate) {
  currentYear.value = props.startDate.year();
  currentMonth.value = props.startDate.month();
} else {
  currentYear.value = dayjs().year();
  currentMonth.value = dayjs().month();
}

const weekDays = (weekNumber: number, startWeek: number, year: number) => {

  return [1, 2, 3, 4, 5, 6, 7].map((i) => {
    const day = dayjs(`${year}-01-01`)
        .add((startWeek + weekNumber - 1) * 7, 'days')
        .day(i)
        .hour(9)
        .minute(0)
        .second(0)
        .millisecond(0);
    day.formatted = day.format('YYYY-MM-DD');
    return day;
  });
};

const togglePreviousMonth = () => {
  if (dayjs().year(currentYear.value).month(currentMonth.value).diff(dayjs()) <= 0) {
    return;
  }
  if (currentMonth.value < 1) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
}

const toggleNextMonth = () => {
  if (currentMonth.value > 10) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
}

const weekNumbers = (year, month) => {
  return dayjs().year(year).month(month).date(0).day() > 5 ? [-1, 0, 1, 2, 3, 4] : (weekCount(year, month + 1) === 7 ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4]);
};

const selectHoverDay = (day) => {
  if (dateRange.value.startDate || selectedDay.value) {
    const dateFrom = dateRange.value.startDate ? dateRange.value.startDate : selectedDay.value;
    hoverDay.value = getPossibleAvailableDay(dateFrom, day);
  }
};

const selectDay = (day) => {
  if (props.prevent_select === true && emit('clickAction') !== undefined) {
    emit('clickAction');
  } else if (props.prevent_select === true) {
    return;
  }
  if (selectedDay.value !== null) {
    if (props.minDuration && dayjs(day).diff(selectedDay.value, 'day') < props.minDuration) {
      return;
    } else if (isDateReserved(day.formatted)) {
      return;
    }
    day = getPossibleAvailableDay(selectedDay.value, day);

    if (dayjs(day).diff(selectedDay.value, 'day') < 1) {
      selectedDay.value = null;
      return;
    }

    dateRange.value = {startDate: selectedDay.value, endDate: day};
    selectedDay.value = null;
    emit('onSelect', clearObserverObj(dateRange.value));
  } else {
    dateRange.value = {startDate: null, endDate: null};
    if (!isDateDisabled(day) && !isDateReserved(day.formatted)) {
      preloader.value = true;
      selectedDay.value = day;
      setTimeout(() => {
        preloader.value = false;
      }, 500);
    }
  }
}


defineExpose({
  setSelectedDates
});

</script>

